/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { ChangeEvent } from 'react';
import { useMemo } from 'react';
import ReactCountryFlag from 'react-country-flag';

import { SelectField, SelectOption } from '@coursera/cds-core';

import formStyles from 'bundles/expression-of-interest/components/i18n-phone-number-input/styles/FormStyles';
import type { PhoneNumberRegion } from 'bundles/expression-of-interest/components/i18n-phone-number-input/types';
import { isPhoneNumberRegion } from 'bundles/expression-of-interest/components/i18n-phone-number-input/types';
import { getPhoneNumberRegionToDetailsMapping } from 'bundles/expression-of-interest/components/i18n-phone-number-input/utils';

import _t from 'i18n!nls/account-profile';

const styles = {
  select: css`
    min-width: 88px;

    .cds-select-select {
      .i18n-country-code-select-field-text {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }

      .i18n-country-code-select-field-flag {
        font-size: 32px !important;
        line-height: 1.5rem !important;
      }
    }
  `,
  region: css`
    margin: 0 4px;
  `,
};

const baseStyles = [
  styles.select,
  formStyles.hideChildLabel,
  formStyles.removeTopMarginImmediateChildDiv,
  formStyles.removeChildInputBottomMargin,
];

type CountryCodeSelectFieldProps = {
  textFieldTopOffset: number;
  selectedRegion?: PhoneNumberRegion;
  onRegionChange: (region: PhoneNumberRegion) => void;
};

const CountryCodeSelectField = (props: CountryCodeSelectFieldProps) => {
  const { textFieldTopOffset, selectedRegion, onRegionChange } = props;

  const calculatedStyles = useMemo(() => {
    return [
      ...baseStyles,
      css`
        margin-top: ${textFieldTopOffset}px;
      `,
    ];
  }, [textFieldTopOffset]);

  const options = useMemo(() => {
    const mapping = getPhoneNumberRegionToDetailsMapping();

    return Object.entries(mapping).map(([key, { code, label }]) => {
      return (
        <SelectOption key={key} value={key}>
          <span className="i18n-country-code-select-field-flag" aria-hidden="true">
            <ReactCountryFlag countryCode={key} />
          </span>
          <span className="i18n-country-code-select-field-text">
            <span css={styles.region}>{label || key}</span>
            <span>
              <span aria-hidden="true">(</span>+{code}
              <span aria-hidden="true">)</span>
            </span>
          </span>
        </SelectOption>
      );
    });
    // The options never change so we should only calculate it when the
    // component renders for the first time
  }, []);

  const handleChange = (event: ChangeEvent<{ name?: string; value: unknown }>) => {
    const maybeRegion = event?.target?.value;
    if (typeof maybeRegion === 'string' && isPhoneNumberRegion(maybeRegion)) {
      onRegionChange(maybeRegion);
    }
  };

  const handleOpen = (event: ChangeEvent<unknown>) => {
    const selectFieldElement = event.target as HTMLDivElement;
    // Simulating pressing tab so the focus goes to the first country option
    // This is preferable to using document.querySelector since we would need a timeout
    selectFieldElement.dispatchEvent(new KeyboardEvent('keydown', { key: 'Tab' }));
  };

  return (
    <SelectField
      css={calculatedStyles}
      label={_t('Phone Number Country Code')}
      fullWidth={false}
      value={selectedRegion}
      onChange={handleChange}
      onOpen={handleOpen}
    >
      {options}
    </SelectField>
  );
};

export default CountryCodeSelectField;
